import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildMarkdownOptions } from './chat/markdownOptions';
import axios from 'axios';
import './ChatWindow.css';
import { ChatShell } from './chat/ChatShell.jsx';
import { useChatDisplay } from './chat/useChatDisplay';
import { ChatMessagesList } from './chat/ChatMessagesList.jsx';
import ExplorerLogModal from './ExplorerLogModal';

// Minimal chat window with two modes:
// - Standard RAG -> calls /api/rag-chat
// - Agentic RAG  -> calls /api/agent-messages (SSE)

const BASE_URL = process.env.REACT_APP_SERVER_URL || '';
const COLLECTION_NAME = 'HR_Docs';

// Single agent tool to enable in agentic mode
const SINGLE_AGENT_TOOL_KEY = 'exploreKnowledgeGraph';

// Keep in sync with ChatSettingsModal/ChatWindow tool keys
const ALL_TOOL_KEYS = [
  'relevantText', 'exploreKnowledgeGraph', 'queryExternalDatabase', 'queryKnowledgeBase',
  'searchCollectionKnowledge', 'listDocumentSections', 'retrieveDocumentSections',
  'googleSearch', 'scrapeWebsite', 'getStockInformation',
  'saveTextToFile', 'listFilesInDirectory', 'readFileContent', 'createDirectory', 'listAllMyFiles',
  'analyticsEngine', 'image_annotation', 'generate_image',
];

const makeAllToolsFalse = () => Object.fromEntries(ALL_TOOL_KEYS.map(k => [k, false]));
const makeOnlyToolEnabled = (toolKey) => {
  const base = makeAllToolsFalse();
  if (toolKey && ALL_TOOL_KEYS.includes(toolKey)) base[toolKey] = true;
  return base;
};

const styles = {
  container: { display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #e5e7eb' },
  title: { margin: 0, fontSize: 18, fontWeight: 600 },
  actions: { display: 'flex', gap: 8 },
  modeBtn: { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', cursor: 'pointer' },
  active: { background: '#111827', color: '#fff', borderColor: '#111827' },
  subBar: { padding: '8px 16px', borderBottom: '1px solid #f0f0f0', color: '#555', fontSize: 13 },
  chatPane: { flex: 1, overflow: 'auto', padding: 16, background: '#f9fafb' },
  systemNote: { color: '#6b7280', fontStyle: 'italic' },
  list: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 },
  bubbleBase: { borderRadius: 10, padding: 12, border: '1px solid #e5e7eb', maxWidth: 720, background: '#fff' },
  user: { alignSelf: 'flex-end', background: '#dbeafe', borderColor: '#bfdbfe' },
  assistant: { alignSelf: 'flex-start', background: '#fff' },
  system: { alignSelf: 'center', background: '#f3f4f6', borderStyle: 'dashed' },
  meta: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 },
  role: { fontSize: 12, fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5 },
  time: { fontSize: 12, color: '#6b7280' },
  text: { whiteSpace: 'pre-wrap' },
  error: { color: '#b91c1c', background: '#fee2e2', border: '1px solid #fecaca', padding: '8px 12px', borderRadius: 8, marginBottom: 10 },
  inputBar: { display: 'flex', gap: 8, padding: 12, borderTop: '1px solid #e5e7eb', background: '#fff' },
  input: { flex: 1, padding: 10, borderRadius: 8, border: '1px solid #d1d5db' },
  sendBtn: { padding: '8px 12px', borderRadius: 8, border: '1px solid #111827', background: '#111827', color: '#fff', cursor: 'pointer' },
};

function bubbleStyle(role) {
  const base = { ...styles.bubbleBase };
  if (role === 'user') return { ...base, ...styles.user };
  if (role === 'assistant') return { ...base, ...styles.assistant };
  return { ...base, ...styles.system };
}

function labelForRole(role) {
  if (role === 'assistant') return 'Assistant';
  if (role === 'user') return 'You';
  if (role === 'system') return 'System';
  return role || 'message';
}

function tsToString(ts) {
  try { return new Date(ts).toLocaleString(); } catch { return ''; }
}

export default function PolicyBot({ authUsername, userLLMSettings }) {
  const [messages, setMessages] = useState([]); // [{role, content, timestamp?}]
  const [chatId, setChatId] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Explorer Log Modal state (open when encountering @@UUID@@ tokens)
  const [isExplorerModalOpen, setIsExplorerModalOpen] = useState(false);
  const [selectedExplorerUUID, setSelectedExplorerUUID] = useState(null);
  const [explorerLogData, setExplorerLogData] = useState(null);
  const [isExplorerModalLoading, setIsExplorerModalLoading] = useState(false);
  const [explorerModalError, setExplorerModalError] = useState('');

  // Sidebar state (match ChatWindow behavior)
  const [chatHistory, setChatHistory] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [refreshSidebarKey, setRefreshSidebarKey] = useState(0);

  const readerRef = useRef(null); // For SSE cancel/cleanup

  const llmProviderDefaults = useMemo(() => {
    const p = userLLMSettings?.llmprovider || 'OpenAI';
    const m = userLLMSettings?.model || 'gpt-4o-mini';
    const t = userLLMSettings?.temperature ?? 0.5;
    const mx = userLLMSettings?.max_tokens ?? 2500;
    return { llmProvider: p, model: m, temperature: t, maxTokens: mx };
  }, [userLLMSettings]);

  // Match ChatWindow markdown styling for assistant messages
  // UUID -> Explorer modal opener
  const handleUUIDClick = useCallback(async (uuid) => {
        if (!uuid) return;
        console.log(`UUID clicked: ${uuid}`);
        setSelectedExplorerUUID(uuid); setExplorerModalError(''); setExplorerLogData(null);
        setIsExplorerModalLoading(true); setIsExplorerModalOpen(true);
        try {
            const response = await axios.get(`${BASE_URL}/api/load_log/${uuid}`, { withCredentials: true });
            if (response.data) {
                 console.log("Fetched explorer log data successfully.");
                 setExplorerLogData(response.data);
            } else { throw new Error("No data received from log endpoint."); }
        } catch (error) {
            let msg = `Failed load log ${uuid.substring(0,8)}...`;
            if (error.response?.status === 401) msg += " Unauthorized.";
            else if (error.response?.status === 404) msg = `Log not found: ${uuid.substring(0,8)}...`;
            else if (error.response?.data?.error) msg += ` ${error.response.data.error}`;
            else if (error.message) msg += ` ${error.message}`;
            console.error(`Error fetching log data for UUID ${uuid}:`, error);
            setExplorerModalError(msg);
        } finally { setIsExplorerModalLoading(false); }
    }, [BASE_URL]);

  // Replace @@UUID@@ tokens with clickable spans
  const processNodeForUUID = useCallback((node) => {
    if (typeof node !== 'string') return node;
    const regex = /@@([0-9a-fA-F-]{36})@@/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(node)) !== null) {
      const [full, uuid] = match;
      const start = match.index;
      if (start > lastIndex) parts.push(node.slice(lastIndex, start));
      parts.push(
        <span
          key={`uuid-${uuid}-${start}`}
          className="uuid-link"
          title={`Open Explorer Log ${uuid}`}
          style={{ textDecoration: 'underline', cursor: 'pointer', color: 'var(--info-color)' }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleUUIDClick(uuid); }}
        >
          {full}
        </span>
      );
      lastIndex = start + full.length;
    }
    if (lastIndex < node.length) parts.push(node.slice(lastIndex));
    return parts.length ? parts : node;
  }, [handleUUIDClick]);

  const markdownOptions = useMemo(() => buildMarkdownOptions({
    processNode: processNodeForUUID,
    authUsername,
    BASE_URL,
  }), [authUsername, processNodeForUUID]);

  useEffect(() => () => {
    // Cleanup SSE reader on unmount
    try { readerRef.current?.cancel?.(); } catch {}
  }, []);

  // Fetch chat history (similar to ChatWindow)
  const fetchHistory = useCallback(async () => {
    try {
      // Load user chat history list and filter by collection by fetching details.
      const response = await axios.get(`${BASE_URL}/api/chat-history`, { withCredentials: true });
      const list = Array.isArray(response.data) ? response.data : [];
      if (list.length === 0) {
        setChatHistory([]);
        return;
      }
      console.log('PolicyBot: Fetched chat history list:', list);
      // The list contains a list of chat histories with id, user_query, date, collection
      // We filter to only those with collection === COLLECTION_NAME
      const filtered = list.filter(c => c.collection === COLLECTION_NAME);
      setChatHistory(filtered);
    } catch (err) {
      console.error('PolicyBot: Failed to fetch chat history:', err);
    }
  }, [BASE_URL]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory, refreshSidebarKey]);

  // Virtualized/filtered display consistent with main chat
  const { containerRef, displayed, hasMore, loadMore } = useChatDisplay(messages, true, 15);

  // Simple cancel for streaming (agentic)
  const handleCancel = useCallback(() => {
    try { readerRef.current?.cancel?.(); } catch {}
    setLoading(false);
    setMessages(prev => ([...prev, { role: 'system', content: 'Cancelled by user.' }]));
  }, []);

  // Sidebar/history helpers
  const filteredHistory = chatHistory;

  const handleSelectChat = useCallback(async (chatData) => {
    if (!chatData || !chatData.id) return;
    setSelectedChat(chatData);
    setCurrentChatId(chatData.id);
    setError('');
    try {
      const response = await axios.get(`${BASE_URL}/api/chat-history/${chatData.id}`, { withCredentials: true });
      const data = response.data || {};
      let history = data.chat_history || [];
      if (typeof history === 'string') { try { history = JSON.parse(history); } catch { /* ignore */ } }
      if (!Array.isArray(history)) history = [];
      const cleaned = history.map(m => ({ role: String(m.role || ''), content: String(m.content || '') }));
      setMessages(cleaned);
    } catch (e) {
      console.error('Failed to load chat history by id:', e);
      setError('Failed to load chat history.');
    }
  }, [BASE_URL]);

  const handleNewChat = useCallback(() => {
    setMessages([]);
    setInput('');
    setError('');
    setSelectedChat(null);
    setCurrentChatId(null);
  }, []);

  const newChatButtonText = useCallback(() => '+ New Chat', []);

  const handleSend = useCallback(async () => {
    if (loading) return;
    const trimmed = input.trim();
    if (!trimmed) return;

  // Use raw user message (collection is passed via payload, not injected into content)
  const userMsg = { role: 'user', content: trimmed  };
    const current = [...messages, userMsg];

    setMessages(current);
    setInput('');
    setError('');
    // Agentic RAG only -> SSE to /api/agent-messages
    setLoading(true);
    try {
      const payload = {
        messages: current,
        human_loop: 5,
        provider: llmProviderDefaults.llmProvider || 'OpenAI',
        selectedTools: makeOnlyToolEnabled(SINGLE_AGENT_TOOL_KEY),
        chat_id: chatId,
        system_prompt_id: 83,
      };

      const response = await fetch(`${BASE_URL}/api/agent-messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
        },
        body: JSON.stringify(payload),
        credentials: 'include',
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      if (!response.body) throw new Error('No response body from agent execution.');

      const reader = response.body.getReader();
      readerRef.current = reader;
      const decoder = new TextDecoder();
      let buffer = '';
      let agentOutput = [...current];

      const processStream = async () => {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          let idx;
          while ((idx = buffer.indexOf('\n\n')) !== -1) {
            const event = buffer.substring(0, idx);
            buffer = buffer.substring(idx + 2);
            if (!event.trim()) continue;

            const lines = event.split('\n');
            let eventType = null;
            let data = '';
            for (const line of lines) {
              if (line.startsWith('event: ')) eventType = line.substring(7).trim();
              else if (line.startsWith('data: ')) data = line.substring(6).trim();
            }

            if (eventType && data) {
              try {
                const parsed = JSON.parse(data);
                if (eventType === 'message') {
                  agentOutput.push(parsed);
                  setMessages([...agentOutput]);
                } else if (eventType === 'paused') {
                  agentOutput.push({ role: 'system', content: 'Agent paused. Awaiting input.' });
                  setMessages([...agentOutput]);
                  setLoading(false);
                } else if (eventType === 'complete') {
                  setMessages([...agentOutput]);
                  setLoading(false);
                  // Save chat history with collection HR_Docs
                  try {
                    await axios.post(`${BASE_URL}/api/save-chat-history`, {
                      messages: agentOutput,
                      prompt_name: 'PolicyBot',
                      collection: COLLECTION_NAME,
                    }, { withCredentials: true });
                    // Refresh sidebar to include the newly saved chat
                    fetchHistory();
                  } catch (saveErr) {
                    console.error('Failed to save chat history:', saveErr);
                  }
                  return;
                } else if (eventType === 'error') {
                  setError(parsed.error || parsed.message || 'Agent error.');
                  setLoading(false);
                  return;
                }
              } catch (e) {
                console.error('Error parsing SSE data:', e, 'Raw data:', data);
              }
            }
          }
        }
      };

      await processStream();
    } catch (err) {
      setError(err.message || 'Failed to call agent.');
      setLoading(false);
    }
  }, [input, messages, chatId, llmProviderDefaults.llmProvider, loading, fetchHistory]);

  // Submit handler so Enter and the button work consistently
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!loading && input.trim()) {
      handleSend();
    }
  }, [handleSend, loading, input]);
  const historyProps = useMemo(() => ({
    history: filteredHistory,
    onChatSelect: handleSelectChat,
    activeChatId: currentChatId,
    onNewChat: handleNewChat,
    refreshHistory: () => setRefreshSidebarKey(k => k + 1),
    newChatButtonTitle: newChatButtonText,
    disabled: loading,
  }), [filteredHistory, handleSelectChat, currentChatId, handleNewChat, loading, newChatButtonText]);

  return (
    <>
      <ChatShell
        historyProps={historyProps}
      >
        <div className="chat-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <h2 style={{ margin: 0 }}>PolicyBot</h2>
          <div style={{ display: 'flex', gap: 8 }} />
        </div>
        <div className="chat-subbar" style={{ padding: '6px 0', color: '#555', fontSize: 13 }}>
          Agentic RAG (/api/agent-messages, tool: {SINGLE_AGENT_TOOL_KEY}) • {authUsername ? `User: ${authUsername}` : 'Not authenticated'}
        </div>

        {error && <div className="chat-error-message">{error}</div>}
        {displayed.length === 0 && !error && (
          <div className="empty-chat-placeholder">
            <h2>PolicyBot Chat</h2>
            <p>Select a past chat or start typing below.</p>
            {userLLMSettings ? (
              <p className="settings-info">Using: <strong>{userLLMSettings.llmprovider || 'N/A'}</strong> - {userLLMSettings.model || 'N/A'} (Temp: {typeof userLLMSettings.temperature === 'number' ? userLLMSettings.temperature.toFixed(1) : 'N/A'}, Max Tokens: {typeof userLLMSettings.max_tokens === 'number' ? userLLMSettings.max_tokens.toLocaleString() : 'N/A'})</p>
            ) : (
              <p className="settings-info">LLM settings missing or not loaded.</p>
            )}
          </div>
        )}

        <ChatMessagesList
          messages={displayed}
          loading={loading}
          error={error}
          hasMore={hasMore}
          onLoadMore={loadMore}
          containerRef={containerRef}
          markdownOptions={markdownOptions}
          chatId={chatId}
          showAgentStepButtons={false}
          onUUIDClick={handleUUIDClick}
        />

        <form className="chat-input-bar" onSubmit={handleSubmit}>
          <input className="chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder={'Ask the Agent…'} aria-label="Type your message" />
          <div className="chat-buttons">
            <button type="button" className="chat-button cancel" disabled={!loading} onClick={handleCancel} title="Cancel running request">Cancel</button>
            <button type="submit" className="chat-button send" disabled={loading || !input.trim()} title="Send Message" aria-busy={loading}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                <path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576zm6.787-8.201L1.591 6.602l4.339 2.76z"/>
              </svg>
            </button>
          </div>
        </form>
      </ChatShell>
      {/* Explorer Log Modal */}
      <ExplorerLogModal
          isOpen={isExplorerModalOpen}
          onClose={() => {
              setIsExplorerModalOpen(false);
              setSelectedExplorerUUID(null);
              setExplorerLogData(null);
              setExplorerModalError('');
          }}
          logData={explorerLogData}
          isLoading={isExplorerModalLoading}
          error={explorerModalError}
      />
    </>
  );
}